import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Skeleton, Tooltip, Modal, Button } from 'antd';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// assets
import { ICONS } from '../../../../assets/icons';
import { images } from '../../../../assets/images';

// helpers
import { cn } from '../../../../helper/cn';
import { useAspSelector } from '../../../../test/jest-redux-hooks';

// api
import { ApiContactList, getApiCount } from '../../../campaign/api/Api';

// components
import Loader from '../../../../components/commonComponents/Loader/Index';
import SearchBar from '../../../../components/commonComponents/searchBar';
import ContactViewFilter from '../../../../components/commonComponents/ListViewHeader/ContactViewFilter';
import BroadcastOverview from '../../../../components/commonComponents/broadcastoverview/BroadcastOverview';
import Paginations from '../../../campaign/components/BroadcastSetting/BroadcastView/Paginations';
import ApiContactTable from '../../../campaign/components/ApiCampaign/ApiContactTable';

// constants
import { campaignApiList } from '../../../../constant/app/campaign/campaign';
import { CAMPAIGN_STATUS } from '../../../campaigns/constants/campaigns';

const ApiCampaignMetrics = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentBrand } = useAspSelector((state) => state.app);

  const campaignDetails = location?.state?.item;

  // State management
  const [loading, setLoading] = useState(true);
  const [isLoader, setIsLoader] = useState(false);
  const [overview, setOverview] = useState(null);
  const [contactList, setContactList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchKey, setSearchKey] = useState('');
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [contactDetails, setContactDetails] = useState({});

  /**
   * Fetch campaign overview/metrics
   */
  const fetchOverview = useCallback(async () => {
    if (!currentBrand?.brand_id || !id) return;
    try {
      const res = await getApiCount(currentBrand.brand_id, id);
      if (res?.data) {
        setOverview(res.data[0]);
      }
    } catch (error) {
      console.error('Error fetching API campaign overview:', error);
    }
  }, [currentBrand?.brand_id, id]);

  /**
   * Fetch contact list
   */
  const fetchContacts = useCallback(
    async (p = 1, l = 10, sKey = '', status = []) => {
      if (!currentBrand?.brand_id || !id) return;
      setLoading(true);
      try {
        const payload = {
          page: p,
          limit: l,
          filter: sKey,
          name: sKey,
          status: status.length > 0 ? status : undefined,
        };
        const res = await ApiContactList(currentBrand.brand_id, id, payload);
        if (res?.data) {
          const updatedData = res.data.conversations_contacts?.map((item) => ({
            ...item,
            subcategory_type: 'CUSTOM',
          }));
          setContactList(updatedData || []);
          setTotal(res.data.conversations_contacts_count || 0);
        }
      } catch (error) {
        console.error('Error fetching API campaign contacts:', error);
      } finally {
        setLoading(false);
      }
    },
    [currentBrand?.brand_id, id],
  );

  useEffect(() => {
    fetchOverview();
    fetchContacts(page, pageSize, searchKey, selectedStatus);
  }, [fetchOverview, fetchContacts, page, pageSize, searchKey, selectedStatus]);

  /**
   * Handle search
   */
  const handleSearch = (value) => {
    setSearchKey(value);
    setPage(1);
  };

  /**
   * Handle filter change
   */
  const onFilterChange = (status) => {
    const updatedStatus = selectedStatus.includes(status)
      ? selectedStatus.filter((s) => s !== status)
      : [...selectedStatus, status];
    setSelectedStatus(updatedStatus);
    setPage(1);
  };

  /**
   * Handle pagination
   */
  const handlePageChange = (p) => {
    setPage(p);
  };

  const handlePageChangeRow = (size) => {
    setPageSize(parseInt(size));
    setPage(1);
  };

  const handleContactClick1 = (item) => {
    setContactDetails(item);
    setPageSize(parseInt(item?.value));
    setPage(1);
  };

  /**
   * Handle export to Excel
   */
  const downloadContacts = async () => {
    if (!currentBrand?.brand_id || !id) return;
    setIsLoader(true);
    try {
      const res = await ApiContactList(currentBrand.brand_id, id, {
        limit: total,
        status: selectedStatus.length > 0 ? selectedStatus : undefined,
      });

      if (res?.data?.conversations_contacts) {
        const allContacts = res.data.conversations_contacts;

        const getStatusLabel = (status) => {
          const map = {
            BOUNCED: 'Bounced',
            SENDING: 'Sending',
            SENT: 'Sent',
            DELIVERED: 'Delivered',
            READ: 'Opened',
            REPLIED: 'Lead',
          };
          return map[status] || 'Unknown';
        };

        const formattedContacts = allContacts.map((contact) => ({
          'From Number': contact.from_number,
          'Country Code': contact.phone_country_code,
          'To Number': contact.mobile_number,
          'First Name': contact.first_name,
          'Last Name': contact.last_name,
          Status: getStatusLabel(contact.status),
          'Campaign Type': 'API',
          'Send At': new Date(contact.send_at).toLocaleString('en-IN', {
            timeZone: 'Asia/Kolkata',
            dateStyle: 'medium',
            timeStyle: 'medium',
          }),
        }));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(formattedContacts);
        XLSX.utils.book_append_sheet(wb, ws, 'Contacts');
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });

        const s2ab = (s) => {
          const buf = new ArrayBuffer(s.length);
          const view = new Uint8Array(buf);
          for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
          return buf;
        };

        saveAs(
          new Blob([s2ab(wbout)], { type: 'application/octet-stream' }),
          `${campaignDetails?.name || 'campaign'}_contacts.xlsx`,
        );
      }
    } catch (error) {
      console.error('Error exporting contacts:', error);
    } finally {
      setIsLoader(false);
    }
  };

  const campaignStatus = campaignDetails?.status
    ? CAMPAIGN_STATUS[campaignDetails.status] || CAMPAIGN_STATUS[10]
    : null;

  return (
    <div className="w-full h-full flex flex-col gap-6 bg-white p-6">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/user/settings/api-campaigns')}
            className="flex items-center p-[4px] justify-center rounded-xl bg-white border border-gray-200 hover:bg-gray-50 transition-colors shadow-sm"
          >
            <img
              src={images?.CampaignBackIcon}
              alt="backIcon"
              className="mr-10 ml-10 pointer"
            />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-[600] text-gray-900">
                {campaignDetails?.name || 'Campaign Metrics'}
              </h2>
              {campaignStatus && (
                <div
                  className="px-2.5 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1.5"
                  style={{
                    backgroundColor: campaignStatus.background,
                    color: campaignStatus.color,
                    border: `1px solid ${campaignStatus.border}`,
                  }}
                >
                  {campaignStatus.text}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Overview */}
      <div className="">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Overview</h3>
        <BroadcastOverview list={campaignApiList} Overview={overview} />
      </div>

      {/* Contacts List */}
      <div className="flex-1 bg-white rounded-2xl shadow-sm border border-[#E8E8EA] flex flex-col">
        <div className="p-4 flex flex-col justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Contacts</h3>
          <div className="flex items-center gap-4 justify-between">
            <div className="w-64">
              <SearchBar
                searchKey={searchKey}
                setSearchKey={setSearchKey}
                onChange={handleSearch}
                expandable={true}
              />
            </div>
            <div className="flex">
              <ContactViewFilter
                onFilterChange={onFilterChange}
                selectedStatus={selectedStatus}
              />
              {isLoader ? (
                <Loader Width={24} Height={24} />
              ) : (
                <Tooltip title="Export to Excel">
                  <button onClick={downloadContacts} className="">
                    <img
                      src={ICONS?.Download}
                      alt="export"
                      className="opacity-70"
                    />
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1">
          <Skeleton
            loading={loading && contactList.length === 0}
            active
            paragraph={{ rows: 10 }}
          >
            {contactList.length > 0 ? (
              <div className="min-w-full">
                <ApiContactTable
                  contactList={contactList}
                  isLoading={loading}
                />
              </div>
            ) : (
              <div className="h-64 flex flex-col items-center justify-center text-gray-400">
                <p>No contact logs found for this campaign.</p>
              </div>
            )}
          </Skeleton>
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="p-4 flex">
            <Paginations
              handlePageChange={handlePageChange}
              page={page}
              setPage={setPage}
              count={total}
              pageSize={pageSize}
              setPageSize={setPageSize}
              handlePageChangeRow={handlePageChangeRow}
              handleContactClick1={handleContactClick1}
              contactDetails={contactDetails}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiCampaignMetrics;
